<template>
  <input
    v-model="model"
    class="input"
    placeholder="_"
    type="text"
    inputmode="numeric"
    maxlength="1"
    required
    @keydown="onKeyDown"
    @input="onInput"
    @paste="onPaste"
    @focus="onFocus"
  />
</template>

<script setup lang="ts">
/**
 * PinInputElement.vue
 *
 * Composant pour la saisie d'un seul chiffre d'un code PIN.
 *
 * Comportement attendu :
 * - Taper un chiffre remplit la case et déplace le focus vers la suivante (si existante).
 * - Touche BACKSPACE :
 *    • si champ courant non vide -> on efface le caractère et on reste focus sur ce champ
 *    • si champ courant vide -> on efface le champ précédent (si présent) et on focus dessus
 *    • si premier champ et vide -> ne fait rien.
 * - Flèche gauche / droite : navigation simple entre cases.
 * - Collage : si 6 chiffres collés, l'événement 'paste' est émis au parent.
 */

import '@/assets/css/components/_pin-code.css'

// Événements
const emit = defineEmits(['paste'])

// Modèle
const model = defineModel<string | null>({ required: true })

// fonctions
/**
 * Gère la navigation (Flèches) et la suppression (Backspace).
 * L'événement 'keydown' est utilisé pour intercepter l'action AVANT que la valeur ne change.
 */
function onKeyDown(event: KeyboardEvent) {
  const target = event.target as HTMLInputElement

  // 1. Gestion du BACKSPACE
  if (event.key === 'Backspace') {
    // Si le champ a une valeur, on l'efface simplement.
    if (model.value) {
      model.value = null
      // On évite que le navigateur tente de reculer en historique
      event.preventDefault()
      return
    }

    // Si le champ est vide, on recule vers le précédent (s'il existe)
    const prev = target.previousElementSibling as HTMLInputElement | null
    if (prev) {
      prev.focus()
    }
    return
  }

  // 2. Navigation Flèche GAUCHE
  if (event.key === 'ArrowLeft') {
    event.preventDefault()
    const prev = target.previousElementSibling as HTMLInputElement | null
    if (prev) prev.focus()
    return
  }

  // 3. Navigation Flèche DROITE
  if (event.key === 'ArrowRight') {
    event.preventDefault()
    const next = target.nextElementSibling as HTMLInputElement | null
    if (next) next.focus()
    return
  }
}

/**
 * Gère la saisie de caractère.
 * Déclenché immédiatement après la modification de la valeur.
 */
function onInput(event: Event) {
  const target = event.target as HTMLInputElement
  const val = target.value

  // Validation : on ne garde que les chiffres
  if (!/^\d$/.test(val)) {
    model.value = null // Rejet si ce n'est pas un chiffre
    return
  }

  // Si une valeur valide est entrée, on passe au suivant
  if (val) {
    const next = target.nextElementSibling as HTMLInputElement | null
    if (next) next.focus()
  }
}

/**
 * Sélectionne automatiquement le contenu lors du focus.
 * Permet de remplacer le chiffre existant sans devoir l'effacer d'abord.
 */
function onFocus(event: FocusEvent) {
  ;(event.target as HTMLInputElement).select()
}

/**
 * Gère le collage complet du code.
 */
function onPaste(event: ClipboardEvent) {
  // On laisse le parent gérer le collage global
  event.preventDefault()
  // Extraction du texte collé
  const pastedText = event.clipboardData?.getData('text').trim().replace(/\D/g, '')

  // Si le texte collé contient exactement 6 chiffres, on émet l'événement
  if (pastedText && pastedText.length === 6) {
    emit('paste', pastedText)
  }
}
</script>
